import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../atoms/Button/Button';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { MeasurementCardGrid } from './MeasurementCardGrid';
import { AddMeasurementDialog } from './AddMeasurementDialog';
import { journalEntryQueryKey } from './JournalEntryPhotos';
import { getJournalEntry, unlinkMeasurement } from '../../services/journalService';
import { targetsById } from '../../services/parameterTargetService';
import { useParameterTargets } from '../../hooks/useParameterTargets';
import { useTranslation } from '../../i18n/useTranslation';
import './JournalEntryMeasurements.css';

export interface JournalEntryMeasurementsProps {
  aquariumId: string;
  entryId: string;
  /**
   * Cards only — no add/unlink actions, nothing rendered while loading or
   * when empty. Used inside JournalEntryForm, where the dialog's own <form>
   * would otherwise be nested in the entry <form>.
   */
  readOnly?: boolean;
}

/**
 * The water-parameter measurements attached to a saved journal entry: a card
 * grid plus (unless read-only) "Add measurement" (AddMeasurementDialog) and
 * unlink. Shares the entry query with JournalEntryPhotos and the detail page.
 */
export function JournalEntryMeasurements({ aquariumId, entryId, readOnly = false }: JournalEntryMeasurementsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ key: string; isError: boolean } | null>(null);

  const { data: entry, isLoading, isError } = useQuery({
    queryKey: journalEntryQueryKey(aquariumId, entryId),
    queryFn: () => getJournalEntry(aquariumId, entryId),
    enabled: Boolean(aquariumId && entryId),
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: journalEntryQueryKey(aquariumId, entryId) });
  }, [aquariumId, entryId, queryClient]);

  const handleUnlink = useCallback(
    async (measurementId: string) => {
      setUnlinkingId(measurementId);
      try {
        await unlinkMeasurement(aquariumId, entryId, measurementId);
        setMessage({ key: 'journal.measurements.unlinked', isError: false });
        refresh();
      } catch {
        setMessage({ key: 'journal.measurements.unlinkError', isError: true });
      } finally {
        setUnlinkingId(null);
      }
    },
    [aquariumId, entryId, refresh],
  );

  const handleLinked = useCallback(() => {
    setMessage({ key: 'journal.measurements.linked', isError: false });
    refresh();
  }, [refresh]);

  const measurements = entry?.attachedMeasurements ?? [];
  const targetsQuery = useParameterTargets(measurements.length > 0 ? aquariumId : null);
  const targets = targetsQuery.data ? targetsById(targetsQuery.data) : undefined;

  if (readOnly) {
    return measurements.length > 0 ? <MeasurementCardGrid measurements={measurements} targets={targets} /> : null;
  }

  let body;
  if (isLoading) {
    body = <Text color="muted">{t('journal.measurements.loading')}</Text>;
  } else if (isError) {
    body = (
      <Alert compact>{t('journal.measurements.loadError')}</Alert>
    );
  } else if (measurements.length === 0) {
    body = (
      <Text color="muted" data-testid="journal-entry-measurements-empty">
        {t('journal.measurements.empty')}
      </Text>
    );
  } else {
    body = (
      <MeasurementCardGrid
        measurements={measurements}
        onUnlink={(id) => void handleUnlink(id)}
        unlinkingId={unlinkingId}
        targets={targets}
      />
    );
  }

  return (
    <div className="journal-entry-measurements" data-testid="journal-entry-measurements">
      {body}
      {message?.isError && <Alert compact>{t(message.key)}</Alert>}
      <LiveRegion className="journal-entry-measurements__status">
        {message && !message.isError && (
          <Text size="sm" color="secondary">
            {t(message.key)}
          </Text>
        )}
      </LiveRegion>
      <div>
        <Button type="button" variant="secondary" onClick={() => setDialogOpen(true)} disabled={isLoading || isError}>
          {t('journal.measurements.add')}
        </Button>
      </div>
      <AddMeasurementDialog
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        aquariumId={aquariumId}
        entryId={entryId}
        linkedMeasurementIds={measurements.map((m) => m.id)}
        onLinked={handleLinked}
      />
    </div>
  );
}
